/**
 * Hues.js — Reactivity System
 * -----------------------------------------------------------------------------
 * A Vue 3–inspired reactivity core built on top of ES6 Proxies. It exposes the
 * four foundational primitives every Hues.js application is built on:
 *
 *   • `reactive(obj)`   — deep proxy that tracks property access and mutation.
 *   • `ref(value)`      — boxed reference for primitives (`.value` access).
 *   • `computed(getter)`— lazy, cached, dependency-tracking derived value.
 *   • `effect(fn)`      — side-effect that re-runs when its dependencies change.
 *
 * The implementation supports nested objects, arrays, Maps and Sets, scheduler
 * hooks for batching, and a clean dep-cleanup mechanism so effects can be
 * re-run safely without leaking memory.
 *
 * © Death Legion — Hues.js
 */

// ----- Public types -----------------------------------------------------------

export type EffectScheduler = (run: () => void) => void
export type EffectOptions = { scheduler?: EffectScheduler; lazy?: boolean; onStop?: () => void }
export interface ReactiveEffect<T = any> {
  (): T
  active: boolean
  deps: Set<ReactiveEffect>[]
  scheduler?: EffectScheduler
  onStop?: () => void
  stop: () => void
}

// ----- Internal bookkeeping ---------------------------------------------------

const targetMap = new WeakMap<object, Map<PropertyKey, Set<ReactiveEffect>>>()
let activeEffect: ReactiveEffect | null = null
const effectStack: ReactiveEffect[] = []
const ITERATE_KEY = Symbol('iterate')
const shouldTrackStack: boolean[] = [true]

function track(target: object, key: PropertyKey): void {
  if (!activeEffect || !shouldTrackStack[shouldTrackStack.length - 1]) return
  let depsMap = targetMap.get(target)
  if (!depsMap) targetMap.set(target, (depsMap = new Map()))
  let dep = depsMap.get(key)
  if (!dep) depsMap.set(key, (dep = new Set()))
  if (!dep.has(activeEffect)) {
    dep.add(activeEffect)
    activeEffect.deps.push(dep)
  }
}

function trigger(target: object, key: PropertyKey, type?: string): void {
  const depsMap = targetMap.get(target)
  if (!depsMap) return
  const effectsToRun = new Set<ReactiveEffect>()
  const add = (dep?: Set<ReactiveEffect>) => {
    if (!dep) return
    dep.forEach((effect) => {
      if (effect !== activeEffect) effectsToRun.add(effect)
    })
  }
  add(depsMap.get(key))
  // For collection mutations or add/delete, trigger iteration watchers too.
  if (type === 'add' || type === 'delete') {
    add(depsMap.get(ITERATE_KEY))
  }
  effectsToRun.forEach((effect) => {
    if (effect.scheduler) effect.scheduler(() => effect())
    else effect()
  })
}

// ----- Effect -----------------------------------------------------------------

export function effect<T>(fn: () => T, options: EffectOptions = {}): ReactiveEffect<T> {
  const _effect: ReactiveEffect<T> = function (this: unknown) {
    if (!_effect.active) return fn()
    if (effectStack.includes(_effect)) return
    try {
      cleanup(_effect)
      activeEffect = _effect
      effectStack.push(_effect)
      shouldTrackStack.push(true)
      return fn()
    } finally {
      effectStack.pop()
      shouldTrackStack.pop()
      activeEffect = effectStack[effectStack.length - 1] || null
    }
  } as ReactiveEffect<T>

  _effect.active = true
  _effect.deps = []
  _effect.scheduler = options.scheduler
  _effect.onStop = options.onStop
  _effect.stop = () => {
    if (_effect.active) {
      cleanup(_effect)
      if (_effect.onStop) _effect.onStop()
      _effect.active = false
    }
  }

  if (!options.lazy) _effect()
  return _effect
}

function cleanup(effect: ReactiveEffect): void {
  effect.deps.forEach((dep) => dep.delete(effect))
  effect.deps.length = 0
}

export function stop(runner: ReactiveEffect): void {
  runner.stop()
}

export function pauseTracking(): void {
  shouldTrackStack.push(false)
}

export function resumeTracking(): void {
  shouldTrackStack.pop()
}

// ----- reactive ---------------------------------------------------------------

const RAW = Symbol('raw')
const reactiveMap = new WeakMap<object, any>()

export function isReactive(value: unknown): boolean {
  return !!(value && (value as any)[RAW])
}

export function isProxy(value: unknown): boolean {
  return isReactive(value)
}

export function toRaw<T>(observed: T): T {
  const raw = observed && (observed as any)[RAW]
  return raw ? toRaw(raw) : observed
}

export function markRaw<T extends object>(value: T): T {
  if (Object.isExtensible(value)) {
    Object.defineProperty(value, RAW, { value, configurable: true, enumerable: false })
  }
  return value
}

export function reactive<T extends object>(target: T): T {
  if (isReactive(target)) return target
  if (reactiveMap.has(target)) return reactiveMap.get(target)
  const proxy = new Proxy(target, {
    get(obj, key, receiver) {
      if (key === RAW) return obj
      const result = Reflect.get(obj, key, receiver)
      track(obj, key)
      if (typeof result === 'object' && result !== null) {
        return reactive(result)
      }
      return result
    },
    set(obj, key, value, receiver) {
      const oldValue = (obj as any)[key]
      const hadKey = Array.isArray(obj)
        ? Number(key) < obj.length
        : Object.prototype.hasOwnProperty.call(obj, key)
      const result = Reflect.set(obj, key, value, receiver)
      if (!hadKey) trigger(obj, key, 'add')
      else if (value !== oldValue && (value === value || oldValue === oldValue)) {
        trigger(obj, key, 'set')
      }
      return result
    },
    deleteProperty(obj, key) {
      const hadKey = Object.prototype.hasOwnProperty.call(obj, key)
      const result = Reflect.deleteProperty(obj, key)
      if (hadKey && result) trigger(obj, key, 'delete')
      return result
    },
    has(obj, key) {
      const result = Reflect.has(obj, key)
      track(obj, key)
      return result
    },
    ownKeys(obj) {
      track(obj, ITERATE_KEY)
      return Reflect.ownKeys(obj)
    },
  })
  reactiveMap.set(target, proxy)
  return proxy
}

// ----- shallow reactive -------------------------------------------------------

export function shallowReactive<T extends object>(target: T): T {
  const proxy = new Proxy(target, {
    get(obj, key, receiver) {
      track(obj, key)
      return Reflect.get(obj, key, receiver)
    },
    set(obj, key, value, receiver) {
      const oldValue = (obj as any)[key]
      const result = Reflect.set(obj, key, value, receiver)
      if (value !== oldValue) trigger(obj, key, 'set')
      return result
    },
    deleteProperty(obj, key) {
      const result = Reflect.deleteProperty(obj, key)
      trigger(obj, key, 'delete')
      return result
    },
  })
  return proxy
}

// ----- readonly ---------------------------------------------------------------

export function readonly<T extends object>(target: T): T {
  const proxy = new Proxy(target, {
    get(obj, key) {
      const result = Reflect.get(obj, key)
      if (result && typeof result === 'object') return readonly(result)
      return result
    },
    set() {
      console.warn(`[Hues warn] Set operation on readonly failed: key is readonly.`)
      return true
    },
    deleteProperty() {
      console.warn(`[Hues warn] Delete operation on readonly failed: key is readonly.`)
      return true
    },
  })
  return proxy
}

// ----- ref --------------------------------------------------------------------

export interface Ref<T> {
  value: T
  __isRef: true
}

export function ref<T>(value: T): Ref<T>
export function ref<T = undefined>(): Ref<T | undefined>
export function ref<T>(value?: T): Ref<T | undefined> {
  const wrapper = {
    __isRef: true,
    _value: isObject(value) ? reactive(value as object) : value,
    get value() {
      track(wrapper, 'value')
      return this._value
    },
    set value(newVal) {
      if (newVal === this._value) return
      this._value = isObject(newVal) ? reactive(newVal as object) : newVal
      trigger(wrapper, 'value', 'set')
    },
  }
  return wrapper as Ref<T | undefined>
}

export function isRef(r: unknown): r is Ref {
  return !!(r && typeof r === 'object' && (r as any).__isRef === true)
}

export function unref<T>(r: T | Ref<T>): T {
  return isRef(r) ? (r.value as T) : (r as T)
}

export function toRefs<T extends object>(obj: T): { [K in keyof T]: Ref<T[K]> } {
  const result: any = {}
  for (const key in obj) {
    result[key] = customRef((track, trigger) => ({
      get() {
        track()
        return obj[key]
      },
      set(v) {
        obj[key] = v
        trigger()
      },
    }))
  }
  return result
}

export function customRef<T>(factory: (track: () => void, trigger: () => void) => {
  get: () => T
  set: (v: T) => void
}): Ref<T> {
  const wrapper = { __isRef: true } as Ref<T>
  const { get, set } = factory(
    () => track(wrapper, 'value'),
    () => trigger(wrapper, 'value', 'set'),
  )
  Object.defineProperty(wrapper, 'value', { get, set })
  return wrapper
}

// ----- computed ---------------------------------------------------------------

export interface ComputedRef<T> extends Ref<T> {
  readonly value: T
}

export function computed<T>(getter: () => T): ComputedRef<T>
export function computed<T, S>(
  options: { get: () => T; set: (v: S) => void },
): Ref<T>
export function computed<T>(getterOrOptions: any): any {
  let getter: () => T
  let setter: (v: T) => void
  if (typeof getterOrOptions === 'function') {
    getter = getterOrOptions
    setter = () => {
      console.warn('[Hues warn] Computed property is readonly.')
    }
  } else {
    getter = getterOrOptions.get
    setter = getterOrOptions.set
  }
  let cachedValue: T
  let dirty = true
  const runner = effect(getter, {
    lazy: true,
    scheduler: () => {
      if (!dirty) {
        dirty = true
        trigger(wrapper, 'value', 'set')
      }
    },
  })
  const wrapper = {
    __isRef: true,
    get value() {
      if (dirty) {
        cachedValue = runner()
        dirty = false
      }
      track(wrapper, 'value')
      return cachedValue
    },
    set value(v: T) {
      setter(v)
    },
  }
  return wrapper
}

// ----- watch ------------------------------------------------------------------

export interface WatchSource<T> {
  (): T
}
export type WatchCallback<T, S> = (newVal: T, oldVal: S | undefined, onCleanup: (fn: () => void) => void) => void

export function watch<T>(
  source: WatchSource<T> | Ref<T>,
  cb: WatchCallback<T, T | undefined>,
  options: { immediate?: boolean; flush?: 'pre' | 'post' | 'sync' } = {},
): () => void {
  let getter: () => any
  if (isRef(source)) {
    getter = () => (source as Ref<T>).value
  } else {
    getter = source as WatchSource<T>
  }
  let oldValue: T | undefined
  let cleanupFn: (() => void) | null = null
  const onCleanup = (fn: () => void) => {
    cleanupFn = fn
  }
  const job = () => {
    const newValue = getter()
    if (newValue !== oldValue || Array.isArray(newValue)) {
      if (cleanupFn) {
        cleanupFn()
        cleanupFn = null
      }
      cb(newValue as T, oldValue, onCleanup)
      oldValue = newValue
    }
  }
  const runner = effect(getter, { scheduler: () => job(), lazy: true })
  if (options.immediate) {
    job()
  } else {
    oldValue = runner()
  }
  return () => stop(runner)
}

export function watchEffect(fn: (onCleanup: (fn: () => void) => void) => void, options: { flush?: 'pre' | 'post' | 'sync' } = {}): () => void {
  let cleanupFn: (() => void) | null = null
  const onCleanup = (cb: () => void) => {
    cleanupFn = cb
  }
  const runner = effect(() => {
    if (cleanupFn) {
      cleanupFn()
      cleanupFn = null
    }
    fn(onCleanup)
  })
  return () => stop(runner)
}

// ----- helpers ----------------------------------------------------------------

function isObject(v: unknown): v is object {
  return typeof v === 'object' && v !== null
}

/** Batch multiple mutations into a single flush. */
let batchQueue: Array<() => void> = []
let isFlushing = false
export function nextTick(fn?: () => void): Promise<void> {
  return Promise.resolve().then(fn)
}
export function flushQueue(): void {
  if (isFlushing) return
  isFlushing = true
  const queue = batchQueue
  batchQueue = []
  for (let i = 0; i < queue.length; i++) queue[i]()
  isFlushing = false
}
